import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UserSalesGoal {
  id: string;
  user_id: string;
  year: number;
  month: number;
  goal_amount: number;
  currency: string;
  business_unit: string | null;
  created_at: string;
  updated_at: string;
}

interface UserSalesGoalWithName extends UserSalesGoal {
  user_name: string;
}

export function useUserSalesGoals(year: number, month: number, businessUnit?: string | null) {
  const queryClient = useQueryClient();

  const { data: goals, isLoading, error } = useQuery({
    queryKey: ['user-sales-goals', year, month, businessUnit],
    queryFn: async () => {
      // First fetch all goals for the period
      let query = supabase
        .from('user_sales_goals')
        .select('*')
        .eq('year', year)
        .eq('month', month);

      // Filter by business_unit if specified
      if (businessUnit) {
        query = query.eq('business_unit', businessUnit);
      } else {
        // For consolidated view, fetch goals from both US and BR
        query = query.in('business_unit', ['us', 'br']);
      }

      const { data: goalsData, error: goalsError } = await query;

      if (goalsError) throw goalsError;

      // Fetch user names for each goal
      if (!goalsData || goalsData.length === 0) {
        return [] as UserSalesGoalWithName[];
      }

      const userIds = goalsData.map(g => g.user_id);
      const { data: usersData, error: usersError } = await supabase
        .from('app_users')
        .select('id, first_name, last_name, email')
        .in('id', userIds);

      if (usersError) throw usersError;

      const usersMap = new Map(usersData?.map(u => [
        u.id,
        `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Unknown'
      ]));

      return goalsData.map(goal => ({
        ...goal,
        user_name: usersMap.get(goal.user_id) || 'Unknown'
      })) as UserSalesGoalWithName[];
    },
  });

  // Calculate total goal amount
  const totalGoal = goals?.reduce((sum, g) => sum + (parseFloat(String(g.goal_amount)) || 0), 0) ?? 0;

  // Upsert a single user's goal
  const upsertGoal = useMutation({
    mutationFn: async ({ userId, goalAmount }: { userId: string; goalAmount: number }) => {
      const { data, error } = await supabase
        .from('user_sales_goals')
        .upsert(
          {
            user_id: userId,
            year,
            month,
            goal_amount: goalAmount,
            business_unit: businessUnit || null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,year,month,business_unit' }
        )
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-sales-goals', year, month, businessUnit] });
      queryClient.invalidateQueries({ queryKey: ['sales-goals-projection'] });
    },
  });

  return {
    goals: goals ?? [],
    totalGoal,
    isLoading,
    error,
    upsertGoal: upsertGoal.mutate,
    isUpdating: upsertGoal.isPending,
  };
}

// Hook to get all sales consultants for the settings page
export function useSalesConsultants() {
  return useQuery({
    queryKey: ['sales-consultants'],
    queryFn: async () => {
      // Fetch all active app_users who are in sales roles
      const { data, error } = await supabase
        .from('app_users')
        .select(`
          id,
          first_name,
          last_name,
          email,
          position,
          active,
          primary_business_unit,
          system_role:system_roles(id, name, code)
        `)
        .eq('active', true)
        .order('first_name');

      if (error) throw error;

      // Filter to users with sales-related roles
      const salesRoles = ['sales_consultant', 'sales_development', 'sales_director', 'admin'];
      return data?.filter(user => {
        const roleCode = (user.system_role as any)?.code;
        return salesRoles.includes(roleCode);
      }) ?? [];
    },
  });
}
